import { useCallback, useEffect, useId, useMemo, useState } from 'react';
import { AccessDeniedNotice } from '../../../shared/AccessDeniedNotice';
import { RetryableError } from '../../../shared/RetryableError';
import { healthcareErrorMessage, isForbidden } from '../healthcareErrors';
import { healthcareRatesGateway } from '../index';
import { payBasisLabels, type CreateServiceRateInput, type PayBasis, type ServiceRate, type ServiceType } from '../types';
import { useSupportedPayBases } from '../useSupportedPayBases';
import { RatesTable } from './RatesTable';
import { ServiceRateForm, type NamedOption } from './ServiceRateForm';
import '../../job-roles/job-roles.css';
import '../healthcare-rates.css';

export interface CompanyRatesSectionProps {
  disciplines: NamedOption[];
  serviceTypes: ServiceType[];
  locations: NamedOption[];
  /** `workrates.write`, as the capability read says it (null = unknown, so offered and left to the server). */
  canManage: boolean;
  /** Lets the page open the add form from its action bar: each change of the number opens it once. */
  addRequest?: number | undefined;
  /** Reports how many of the company's rates are in effect today (only while the list is unfiltered). */
  onActiveCount?: ((count: number) => void) | undefined;
}

/** How a rate is chosen, in the order the resolver tries it (contract section 7). Plain language, no amounts. */
export function PrecedenceExplainer() {
  return (
    <div className="hdc-explainer" data-testid="precedence-explainer">
      <h4>How a rate is chosen</h4>
      <p className="jr-hint">When a pay-run line is calculated, EPN looks for the rate in this order and uses the first one that applies on the service date:</p>
      <ol>
        <li>The employee&apos;s own rate for the worker category and pay basis. A rate for the exact work type beats &ldquo;Any work type&rdquo;, then a rate for the exact location beats &ldquo;All locations&rdquo;.</li>
        <li>Your company&apos;s rate for the line&apos;s work location (the exact work type first).</li>
        <li>Your company&apos;s default rate for all locations (the exact work type first).</li>
        <li>The employee&apos;s base rate, only when its pay basis is the same as the line&apos;s.</li>
      </ol>
      <p className="jr-hint">
        In each company step, a rate that names the worker&apos;s own category is used before a rate for &ldquo;Any worker category&rdquo;, and the exact work type before &ldquo;Any work type&rdquo;.
      </p>
      <p className="jr-hint">
        If two equally specific rates apply on the same day, the line is refused as ambiguous: EPN never picks one for you. If no rate applies, the line is refused: a rate of zero is never
        used. The effective rate is the rate plus any differential (a fixed amount, or a percentage of the rate), kept to up to 4 decimals; only the amount of a line is rounded to the cent.
      </p>
    </div>
  );
}

/**
 * The company rate schedule (PayrollConfig): what the company pays for each discipline, service type, location and pay basis. Rows are effective-dated and append-only:
 * to change a rate, end the old one and add a new one. Two rows with the same scope may not overlap in time; the server refuses it (RATE_OVERLAP) and the form shows the row
 * it overlaps. Nothing here comes from a payer's reimbursement table.
 */
export function CompanyRatesSection({ disciplines, serviceTypes, locations, canManage, addRequest, onActiveCount }: CompanyRatesSectionProps) {
  const uid = useId();
  const payBases = useSupportedPayBases();
  const [lastRequest, setLastRequest] = useState(addRequest ?? 0);
  const [rows, setRows] = useState<ServiceRate[] | null>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'forbidden' | 'error'>('loading');
  const [message, setMessage] = useState('');
  const [nonce, setNonce] = useState(0);
  const [disciplineId, setDisciplineId] = useState('');
  const [serviceTypeId, setServiceTypeId] = useState('');
  const [workLocationId, setWorkLocationId] = useState('');
  const [payBasis, setPayBasis] = useState<PayBasis | ''>('');
  const [includeEnded, setIncludeEnded] = useState(false);
  const [adding, setAdding] = useState(false);
  const [status, setStatus] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const reload = useCallback(() => setNonce((n) => n + 1), []);

  // The page's "Add Company Rate" button asks this section to open its form (derived from props during render, not an effect).
  if ((addRequest ?? 0) !== lastRequest) {
    setLastRequest(addRequest ?? 0);
    if (canManage) { setAdding(true); setStatus(null); }
  }

  useEffect(() => {
    const controller = new AbortController();
    healthcareRatesGateway
      .listServiceRates({
        ...(disciplineId ? { disciplineId } : {}), ...(serviceTypeId ? { serviceTypeId } : {}), ...(workLocationId ? { workLocationId } : {}),
        ...(payBasis ? { payBasis } : {}), includeEnded,
      }, controller.signal)
      .then((list) => { if (!controller.signal.aborted) { setRows(list); setState('ready'); } })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        setState(isForbidden(err) ? 'forbidden' : 'error');
        setMessage(healthcareErrorMessage(err, 'Could not load the company rates.'));
      });
    return () => controller.abort();
  }, [disciplineId, serviceTypeId, workLocationId, payBasis, includeEnded, nonce]);

  // The unfiltered count of rates in effect today, for the page's summary (only while no filter is applied, so the number never describes a narrowed list).
  const unfiltered = !disciplineId && !serviceTypeId && !workLocationId && !payBasis && !includeEnded;
  useEffect(() => {
    if (state === 'ready' && rows && unfiltered) onActiveCount?.(rows.filter((r) => r.isActive).length);
  }, [state, rows, unfiltered, onActiveCount]);

  const locationNames = useMemo(() => new Map(locations.map((l) => [l.id, l.name])), [locations]);
  const activeTypes = useMemo(() => serviceTypes.filter((t) => t.isActive), [serviceTypes]);

  async function create(input: CreateServiceRateInput) {
    await healthcareRatesGateway.createServiceRate(input);
    setAdding(false);
    setStatus('Company rate saved.');
    reload();
  }

  async function end(rate: ServiceRate, date: string) {
    setActionError(null);
    await healthcareRatesGateway.endServiceRate(rate.id, date, rate.concurrencyVersion);
    setStatus(date <= rate.effectiveFrom ? 'Company rate voided: it never applied.' : 'Company rate ended. Add a new rate to replace it.');
    reload();
  }

  return (
    <section className="hdc-section" aria-labelledby={`${uid}-title`} id="company-rates">
      <div className="section-header">
        <h3 id={`${uid}-title`}>Company rates</h3>
        {canManage && state === 'ready' && !adding && (
          <button type="button" className="jr-btn jr-btn--primary" onClick={() => { setAdding(true); setStatus(null); }}><span aria-hidden="true">+ </span>Add a company rate</button>
        )}
      </div>
      <p className="jr-hint">
        What your company pays for each worker category, work type, location and pay basis. Rates are dated. To change one, end it and add a new one, so history is never rewritten.
      </p>
      {!canManage && state === 'ready' && <p className="jr-readonly" role="note">You can see the rate schedule but not change it.</p>}
      <PrecedenceExplainer />
      {status && <p className="jr-ok" role="status">{status}</p>}
      {actionError && <p className="jr-error jr-error--block" role="alert">{actionError}</p>}

      {adding && (
        <ServiceRateForm
          title="Add a company rate"
          disciplines={disciplines}
          serviceTypes={activeTypes}
          locations={locations}
          existing={rows ?? []}
          submitLabel="Save company rate"
          allowAnyWorkerCategory
          onSubmit={create}
          onCancel={() => setAdding(false)}
        />
      )}

      <div className="jr-toolbar">
        <div className="jr-filters" role="group" aria-label="Rate filters">
          <div className="jr-field">
            <label htmlFor={`${uid}-f-disc`}>Worker category</label>
            <select id={`${uid}-f-disc`} value={disciplineId} onChange={(e) => setDisciplineId(e.target.value)}>
              <option value="">All worker categories</option>
              {disciplines.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          </div>
          <div className="jr-field">
            <label htmlFor={`${uid}-f-svc`}>Work type</label>
            <select id={`${uid}-f-svc`} value={serviceTypeId} onChange={(e) => setServiceTypeId(e.target.value)}>
              <option value="">All work types</option>
              {serviceTypes.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>
          <div className="jr-field">
            <label htmlFor={`${uid}-f-loc`}>Location</label>
            <select id={`${uid}-f-loc`} value={workLocationId} onChange={(e) => setWorkLocationId(e.target.value)}>
              <option value="">All locations</option>
              {locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
            </select>
          </div>
          <div className="jr-field">
            <label htmlFor={`${uid}-f-basis`}>Pay basis</label>
            <select id={`${uid}-f-basis`} value={payBasis} onChange={(e) => setPayBasis(e.target.value as PayBasis | '')}>
              <option value="">All pay bases</option>
              {payBases.map((b) => <option key={b} value={b}>{payBasisLabels[b]}</option>)}
            </select>
          </div>
          <label className="hdc-check">
            <input type="checkbox" checked={includeEnded} onChange={(e) => setIncludeEnded(e.target.checked)} />
            <span>Show ended rates</span>
          </label>
        </div>
      </div>

      {state === 'loading' && <p className="muted" role="status">Loading company rates…</p>}
      {state === 'forbidden' && <AccessDeniedNotice requiredPermissionLabel="permission to view pay rates (workrates.read)" />}
      {state === 'error' && <RetryableError message={message} onRetry={reload} />}
      {state === 'ready' && rows && (
        <RatesTable
          label="Company rate schedule"
          rows={rows}
          locationNames={locationNames}
          onEnd={canManage ? end : undefined}
          emptyMessage="No company rates match. Add a rate, or change the filters."
          markExamples
        />
      )}
    </section>
  );
}
